/**
 * Client health score — guide §6.1, ADR-001 §8.1, §8.5. The prototype's `health()` (docs/prototype.html L2263,
 * with the L2461 `mailTone` override) is the specification; the numbers come from Settings → thresholds.
 *
 * Pure: the API (scoring a never-scored client on read) and the worker (`health.recompute`) call this same
 * function with the same inputs, so a stored snapshot and a live read can never disagree.
 *
 * Every point taken or given is one reason line `{ signal, penalty, detail, band }`, worst rule first in the
 * prototype's order, so 100 + Σ penalty is the score before the floor/cap. Tone points are capped as a group
 * (`tone_cap`); the reason lines carry the points actually taken, so the sum still adds up.
 */
import { HEALTH_BAND_LABEL, TONE_SIGNALS, } from '@amplispot/contracts';
const EMAIL_UNAVAILABLE_DETAIL = (e) => {
    const who = e.spoc_first_name ?? 'The SPOC';
    if (e.why === 'no_spoc')
        return 'No email signals — this client has no SPOC';
    if (e.why === 'broken')
        return `No email signals — ${who}'s email connection has stopped working`;
    return `No email signals — ${who} has not connected email`;
};
const BAND = { cold: 'bad', warm: 'warn', hot: 'ok', info: 'ok' };
const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;
/** The prototype's `clip`: at most `n` characters, ending in an ellipsis when cut. */
export const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trim()}…` : s);
/** JS `Math.round` (half toward +∞) — the SQL twin `health_weighted` uses floor(x + 0.5) to match. */
const jsRound = (x) => Math.round(x);
/**
 * Revenue-weighted average Σ(v × w) / Σ w over the items that have a value (prototype `utilOf`/`trendOf`).
 * `null` when no item has a value. When the items with a value carry no revenue at all (Σ w = 0, e.g. MRR not
 * entered yet), every item counts equally rather than the score dividing by zero.
 */
export function revenueWeighted(items) {
    const ps = items.filter((p) => p.value != null);
    if (!ps.length)
        return null;
    const w = ps.reduce((a, p) => a + Math.max(0, p.weight ?? 0), 0);
    if (w <= 0)
        return jsRound(ps.reduce((a, p) => a + p.value, 0) / ps.length);
    return jsRound(ps.reduce((a, p) => a + p.value * Math.max(0, p.weight ?? 0), 0) / w);
}
export const bandOf = (score, h) => score >= h.band_healthy ? 'healthy' : score >= h.band_watch ? 'watch' : 'at_risk';
const TONE_WORDS = {
    leave: 'hints at leaving',
    escalate: 'threatens escalation',
    negative: 'sounds unhappy',
    urgent: 'is an urgent ask',
    neutral: 'was flagged',
    positive: 'is positive',
};
/**
 * Score one client. `t.health` holds the lines and points, `t.tone` the per-label points and the on/off switch
 * (off: no tone reason and no tone points for anyone).
 */
export function scoreHealth(src, t) {
    const h = t.health;
    const reasons = [];
    let s = 100;
    const add = (signal, pts, sev, detail, mail = false) => {
        s += pts;
        reasons.push({ signal, ...(signal === 'email_followups' ? {} : { penalty: pts }), detail, band: BAND[sev], ...(mail ? { source: 'mail' } : {}) });
    };
    const u = src.weighted_util;
    const tr = src.weighted_trend;
    if (u != null && u < h.util_low)
        add('util_low', -h.pen_util_low, 'cold', `Utilisation is ${u}% of what they pay for`);
    else if (u != null && u < h.util_watch)
        add('util_watch', -h.pen_util_watch, 'warm', `Utilisation is ${u}%`);
    if (tr < h.trend_fall)
        add('trend_fall', -h.pen_trend_fall, 'cold', `Usage is ${Math.abs(tr)}% below their own baseline`);
    else if (tr < h.trend_dip)
        add('trend_dip', -h.pen_trend_dip, 'warm', `Usage is ${Math.abs(tr)}% below baseline`);
    const od = src.overdue_tasks;
    if (od)
        add('overdue', -Math.min(h.overdue_cap, od * h.pen_overdue_each), od > 1 ? 'cold' : 'warm', plural(od, 'overdue task'));
    const es = src.open_escalations;
    if (es)
        add('escalations', -h.pen_escalation_each * es, 'cold', plural(es, 'open escalation'));
    const sl = src.request_breaches;
    // The prototype's wording is singular whatever the count ("2 request past its response time").
    if (sl)
        add('request_breach', -h.pen_request_breach_each * sl, 'warm', `${sl} request past its response time`);
    // ---- from emails (and meetings, for "quiet"). With no working Gmail on the SPOC, none of these may fire
    // (ADR-003 §6.1 item 1): `mails` is then empty and meeting ages alone would overstate how quiet the client is.
    const email = src.email_signals ?? { available: true };
    if (!email.available)
        add('email_signals_unavailable', 0, 'warm', EMAIL_UNAVAILABLE_DETAIL(email));
    const mails = email.available ? src.mails : [];
    const inbound = mails.filter((m) => m.dir === 'in');
    const ages = [...inbound.map((m) => m.age_days), ...src.meeting_ages].filter((a) => a != null);
    const quiet = ages.length ? Math.min(...ages) : null;
    // No quiet penalty without email: contact by email cannot be seen.
    if (email.available && quiet != null && quiet > h.quiet_days_hard) {
        add('quiet_hard', -h.pen_quiet_hard, 'cold', `Email: client has gone quiet, nothing from them for ${quiet} days`, true);
    }
    else if (email.available && quiet != null && quiet > h.quiet_days_soft) {
        add('quiet_soft', -h.pen_quiet_soft, 'warm', `Email: nothing from the client for ${quiet} days`, true);
    }
    const toneOn = t.tone.enabled;
    const pen = (l) => t.tone.penalties[l];
    const recentIn = inbound.filter((m) => m.age_days != null && m.age_days <= h.tone_window_days);
    const worst = toneOn
        ? recentIn.filter((m) => m.tone && pen(m.tone) > 0).sort((a, b) => pen(b.tone) - pen(a.tone)).slice(0, 2)
        : [];
    let toneLeft = h.tone_cap;
    let tonePenalty = 0;
    for (const m of worst) {
        const p = pen(m.tone);
        const taken = Math.min(p, toneLeft);
        toneLeft -= taken;
        tonePenalty += taken;
        const quote = m.quote ? `: “${clip(m.quote, 70)}”` : '';
        add('tone', -taken, p >= 15 ? 'cold' : 'warm', `Email ${TONE_WORDS[m.tone]}${quote} (${m.when})`, true);
    }
    const needs = mails.filter((m) => m.dir === 'in' && m.state === 'needs' && (m.days ?? 0) >= h.needs_min_days);
    if (needs.length) {
        const oldest = Math.max(...needs.map((m) => m.days ?? 0));
        add('needs_reply', -Math.min(h.needs_cap, h.pen_needs_each * needs.length), 'warm', `Email: ${plural(needs.length, 'client email')} waiting for our reply (oldest ${oldest} days)`, true);
    }
    const noReply = mails.filter((m) => m.dir === 'out' && m.state === 'waiting' && (m.days ?? 0) >= h.noreply_days);
    if (noReply.length) {
        const what = noReply.length > 1 ? `${noReply.length} of our emails` : 'our email';
        add('no_reply', -h.pen_noreply, 'warm', `Email: client has not replied to ${what} for ${Math.max(...noReply.map((m) => m.days ?? 0))} days`, true);
    }
    const positive = toneOn ? recentIn.filter((m) => m.tone === 'positive') : [];
    if (positive.length)
        add('positive_tone', h.bonus_positive, 'hot', `Email: positive note from the client (${positive[0].when})`, true);
    const fu = src.email_followups;
    if (fu.open)
        add('email_followups', 0, fu.any_overdue ? 'warm' : 'info', `${fu.open} follow-up${fu.open > 1 ? 's' : ''} from emails still open`, true);
    if (src.products_no_data > 0)
        add('no_data', -h.pen_no_data, 'warm', 'Usage data missing on one product');
    const score = Math.max(h.floor, Math.min(100, s));
    const band = bandOf(score, h);
    const needsReply = needs.length;
    return {
        score,
        band,
        label: HEALTH_BAND_LABEL[band],
        reasons,
        inputs: {
            weighted_util: u,
            weighted_trend: tr,
            overdue_tasks: od,
            open_escalations: es,
            request_breaches: sl,
            quiet_days: quiet,
            needs_reply: needsReply,
            unanswered_outbound: noReply.length,
            products_no_data: src.products_no_data,
            email_followups_open: fu.open,
            tone_penalty: tonePenalty,
            positive_tone: positive.length > 0,
        },
    };
}
const TONE_SET = new Set(TONE_SIGNALS);
/** ADR §8.1/§8.5: the tone REASON is for callers with `toneReason.read`; the score keeps the points. */
export const visibleReasons = (reasons, toneReason) => toneReason ? [...reasons] : reasons.filter((r) => !TONE_SET.has(r.signal));
export function visibleInputs(inputs, toneReason) {
    if (toneReason)
        return inputs;
    const { tone_penalty: _tp, positive_tone: _pt, ...rest } = inputs;
    return rest;
}
//# sourceMappingURL=health.js.map